<template>
  <div class="cbs-html-sandbox">
    <div
      v-if="fatal"
      class="cbs-html-sandbox__error text-muted d-flex align-items-center justify-content-center"
      :style="{ minHeight: minHeight + 'px' }"
    >
      <div class="text-center">
        <feather-icon
          icon="AlertTriangleIcon"
          size="20"
        />
        <div class="mt-50">
          {{ fatal }}
        </div>
      </div>
    </div>
    <iframe
      v-else
      ref="frame"
      :srcdoc="preparedHtml"
      :style="{ height: frameHeight + 'px' }"
      :title="page && page.sid ? page.sid : 'cubus-html-page'"
      class="cbs-html-sandbox__frame"
      sandbox="allow-scripts allow-forms allow-modals"
      referrerpolicy="no-referrer"
    />
  </div>
</template>

<script>
import useCubus from '@/cubus/services/useCubus'
import { buildSandboxHtml, newFrameId } from '@/cubus/html-sandbox/cubusPageSdk'
import {
  bridgeError,
  queryObject,
  saveObjectRecord,
  upsertObjectRecord,
  deleteObjectRecord,
  runProcedure,
} from '@/cubus/html-sandbox/htmlPageApi'

// PROP-048: набор операций моста меняется от сборки к сборке (upsert появился только в 157),
// а способа узнать его до вызова не было — страница, написанная под новый метод, на старом
// фронте падала в рантайме (`cubus.upsert is not a function`), а пользователь видел невнятное
// «Something went wrong CODE 16». Список объявляет ХОСТ, а не SDK: диспетчеризует именно он,
// и обёртка в window.cubus может существовать там, где операции у хоста нет.
// v2 = контекст сообщает protocolversion и operations.
const PROTOCOL_VERSION = 2
const OPERATIONS = [
  'getContext', 'query', 'save', 'upsert', 'delete', 'runProc',
  'setValue', 'navigate', 'toast', 'resize',
]
const READY_TIMEOUT_MS = 10000
const MAX_PAYLOAD_CHARS = 1024 * 1024
const MAX_CONCURRENT = 20
const MAX_REQ_PER_SEC = 30

// Цвета Vuexy для передачи в страницу как --cubus-* переменные
const THEME_COLORS = {
  light: {
    primary: '#7367f0',
    secondary: '#82868b',
    success: '#28c76f',
    danger: '#ea5455',
    warning: '#ff9f43',
    info: '#00cfe8',
    body: '#f8f8f8',
    surface: '#ffffff',
    text: '#6e6b7b',
    heading: '#5e5873',
  },
  dark: {
    primary: '#7367f0',
    secondary: '#82868b',
    success: '#28c76f',
    danger: '#ea5455',
    warning: '#ff9f43',
    info: '#00cfe8',
    body: '#161d31',
    surface: '#283046',
    text: '#b4b7bd',
    heading: '#d0d2d6',
  },
}

export default {
  name: 'CbsHtmlSandbox',
  props: {
    page: {
      type: Object,
      required: true,
    },
    context: {
      type: Object,
      default: null,
    },
    minHeight: {
      type: Number,
      default: 120,
    },
    maxHeight: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      frameId: newFrameId(),
      frameHeight: this.minHeight,
      fatal: null,
      isReady: false,
      readyTimer: null,
      activeReqs: 0,
      reqTimes: [],
    }
  },
  computed: {
    preparedHtml() {
      return buildSandboxHtml(this.page.html, this.frameId)
    },
    skin() {
      return this.$store.state.appConfig.layout.skin === 'dark' ? 'dark' : 'light'
    },
  },
  watch: {
    context: {
      deep: true,
      handler() {
        if (this.isReady) this.sendEvent('contextChanged', this.buildContext())
      },
    },
    skin() {
      if (this.isReady) this.sendEvent('themeChanged', { theme: this.buildTheme() })
    },
  },
  mounted() {
    window.addEventListener('message', this.onMessage)
    this.readyTimer = setTimeout(() => {
      if (!this.isReady) {
        this.fatal = 'Страница не отвечает (нет рукопожатия SDK за 10 с).'
        this.$emit('error', bridgeError('E_TIMEOUT', 'sdk ready timeout'))
      }
    }, READY_TIMEOUT_MS)
  },
  beforeDestroy() {
    if (this.isReady) this.sendEvent('destroy', null)
    if (this.readyTimer) clearTimeout(this.readyTimer)
    window.removeEventListener('message', this.onMessage)
  },
  methods: {
    post(msg) {
      const { frame } = this.$refs
      if (!frame || !frame.contentWindow) return
      const m = msg
      m.cubus = true
      m.v = PROTOCOL_VERSION
      m.frame = this.frameId
      frame.contentWindow.postMessage(m, '*')
    },
    sendEvent(type, payload) {
      this.post({ kind: 'event', type, payload })
    },
    // PROP-050: событие `refresh` описано в SDK (cubus.onRefresh), но послать его было некому —
    // родитель вызывает этот метод, страница перезапрашивает данные внутри уже загруженного
    // iframe. Смена самого html — не сюда: srcdoc инжектится один раз при монтировании,
    // её обрабатывает владелец виджета пересозданием фрейма.
    refresh() {
      if (this.isReady) this.sendEvent('refresh', null)
    },
    respond(id, result) {
      this.post({ kind: 'res', id, result })
    },
    respondError(id, err) {
      this.post({
        kind: 'res',
        id,
        error: {
          code: err.code || 'E_INTERNAL',
          message: err.message || String(err),
          details: err.details !== undefined ? err.details : null,
        },
      })
    },

    onMessage(event) {
      const { frame } = this.$refs
      if (!frame || event.source !== frame.contentWindow) return
      const msg = event.data
      if (!msg || msg.cubus !== true || msg.frame !== this.frameId) return
      if (msg.kind === 'event') {
        this.onFrameEvent(msg)
      } else if (msg.kind === 'req') {
        this.onFrameRequest(msg)
      }
    },

    onFrameEvent(msg) {
      if (msg.type === 'ready') {
        if (this.readyTimer) clearTimeout(this.readyTimer)
        if (msg.payload && Number(msg.payload.v) > PROTOCOL_VERSION) {
          this.fatal = 'Несовместимая версия протокола страницы.'
          this.$emit('error', bridgeError('E_PROTOCOL', 'incompatible protocol version'))
          return
        }
        this.isReady = true
        this.sendEvent('init', this.buildContext())
        this.$emit('ready')
      } else if (msg.type === 'action') {
        const p = msg.payload || {}
        this.$emit('action', p.name, p.data)
      }
    },

    onFrameRequest(msg) {
      if (!msg.id || typeof msg.type !== 'string') return
      try {
        this.checkLimits(msg)
      } catch (err) {
        this.respondError(msg.id, err)
        return
      }
      this.activeReqs += 1
      Promise.resolve()
        .then(() => this.dispatch(msg.type, msg.payload || {}))
        .then(result => this.respond(msg.id, result))
        .catch(err => {
          console.error(`[CbsHtmlSandbox] page="${this.page.sid}" req="${msg.type}" failed:`, err)
          this.respondError(msg.id, err)
        })
        .finally(() => { this.activeReqs -= 1 })
    },

    checkLimits(msg) {
      if (JSON.stringify(msg).length > MAX_PAYLOAD_CHARS) {
        throw bridgeError('E_PAYLOAD_TOO_LARGE', 'message exceeds 1 MB')
      }
      if (this.activeReqs >= MAX_CONCURRENT) {
        throw bridgeError('E_RATE_LIMIT', `more than ${MAX_CONCURRENT} concurrent requests`)
      }
      const now = Date.now()
      this.reqTimes = this.reqTimes.filter(t => now - t < 1000)
      if (this.reqTimes.length >= MAX_REQ_PER_SEC) {
        throw bridgeError('E_RATE_LIMIT', `more than ${MAX_REQ_PER_SEC} requests per second`)
      }
      this.reqTimes.push(now)
    },

    dispatch(type, payload) {
      // Заявленный список — единственный источник правды: новая ветка switch без строки в
      // OPERATIONS будет отвергнута здесь, поэтому обещание странице не разойдётся с делом.
      if (OPERATIONS.indexOf(type) === -1) {
        throw bridgeError('E_UNSUPPORTED', `unknown request type "${type}"`, { operations: OPERATIONS })
      }
      switch (type) {
        case 'getContext': return this.buildContext()
        case 'query':
          this.assertObjectAllowed(payload.objectsid)
          return queryObject(payload)
        case 'save':
          this.assertObjectAllowed(payload.objectsid)
          return saveObjectRecord(payload.objectsid, payload.record)
        case 'upsert':
          this.assertObjectAllowed(payload.objectsid)
          return upsertObjectRecord(payload.objectsid, payload.record, payload.uniqueKeys)
        case 'delete':
          this.assertObjectAllowed(payload.objectsid)
          return deleteObjectRecord(payload.objectsid, payload.record)
        case 'runProc':
          this.assertProcAllowed(payload.procsid)
          return runProcedure(payload.procsid, payload.params)
        case 'setValue':
          return this.handleSetValue(payload)
        case 'navigate':
          return this.handleNavigate(payload)
        case 'toast':
          return this.handleToast(payload)
        case 'resize':
          return this.handleResize(payload)
        default:
          throw bridgeError('E_INTERNAL', `operation "${type}" is declared but not implemented`, { operations: OPERATIONS })
      }
    },

    assertObjectAllowed(objectsid) {
      const list = this.page.objectwhitelist || []
      if (!objectsid || list.indexOf(objectsid) === -1) {
        throw bridgeError('E_DENIED', `object "${objectsid}" is not in page whitelist`)
      }
    },
    assertProcAllowed(procsid) {
      const list = this.page.procwhitelist || []
      if (!procsid || list.indexOf(procsid) === -1) {
        throw bridgeError('E_DENIED', `procedure "${procsid}" is not in page whitelist`)
      }
    },

    handleSetValue(payload) {
      if (!this.context || this.context.mode !== 'form') {
        throw bridgeError('E_UNSUPPORTED', 'setValue is available only in form mode')
      }
      this.$emit('setvalue', payload.attribute, payload.value)
      return { ok: true }
    },

    handleNavigate(payload) {
      const objectid = payload.objectid || payload.objectsid
      if (!objectid) throw bridgeError('E_DENIED', 'only internal object navigation is allowed')
      if (payload.recordid !== undefined && payload.recordid !== null) {
        this.$router.push({ path: `/cubus/recordpage/${objectid}/${payload.recordid}` })
      } else {
        this.$router.push({ name: 'cubus-object', params: { objectid } })
      }
      return { ok: true }
    },

    handleToast(payload) {
      const message = String(payload.message || '')
      if (payload.kind === 'error') useCubus.toastError(this, message)
      else useCubus.toastMessage(this, message)
      return { ok: true }
    },

    handleResize(payload) {
      let h = Number(payload.height) || this.minHeight
      if (h < this.minHeight) h = this.minHeight
      if (this.maxHeight && h > this.maxHeight) h = this.maxHeight
      this.frameHeight = h
      return { ok: true }
    },

    buildTheme() {
      return {
        skin: this.skin,
        colors: THEME_COLORS[this.skin],
      }
    },

    buildContext() {
      const session = this.$store.getters['cubus-store/SESSION']
      const user = session && session.user ? {
        id: session.user.id,
        sid: session.user.sid || null,
        name: session.user.name || null,
        role: session.user.role ? session.user.role.sid || session.user.role.name : null,
      } : null
      const base = {
        mode: 'widget',
        user,
        locale: (this.$i18n && this.$i18n.locale) || 'ru',
        theme: this.buildTheme(),
      }
      // Поля контракта ставятся ПОСЛЕ контекста родителя: заявлять возможности моста может
      // только сам мост, иначе форма или дэшборд могли бы их случайно переопределить.
      return Object.assign(base, this.context || {}, {
        protocol: PROTOCOL_VERSION,
        protocolversion: PROTOCOL_VERSION,
        operations: OPERATIONS,
      })
    },
  },
}
</script>

<style scoped>
.cbs-html-sandbox__frame {
  width: 100%;
  border: 0;
  display: block;
}
.cbs-html-sandbox__error {
  width: 100%;
}
</style>
